import { useEffect, useState } from "react";
import { useQuery } from "@apollo/client";
import { useFactsheetsFilters} from './hooks/useFactsheetsFilters'

import GET_FACTSHEETS from "../../../graphql/queries/IIAs/getFactsheets";
import GET_COUNTRIES from "../../../graphql/queries/registration/getCountries";

import TableSkeleton from "../../../components/Table/components/TableSkeleton";
import CustomTable from "../../../components/Table";
import Filters from "../../../components/Filters";
import ExportFactsheetPdf from "./ExportFactsheetPdf";

import './index.scss'

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec"
]

const FactsheetBrowser = () => {
    const initialState = {
        erasmusCode: "",
        autumnTermDeadline: "",
        springTermDeadline: "",
        country: [],
        sortBy: localStorage.getItem('sortByFactsheets') !== null ? JSON.parse(localStorage.getItem('sortByFactsheets')) : "changed_descending",
        selectedFiltersList: localStorage.getItem('selectedFiltersFactsheets') !== null ? JSON.parse(localStorage.getItem('selectedFiltersFactsheets')) : {
            erasmusCode: "",
            autumnTermDeadline: "",
            springTermDeadline: "",
            country: []
        },
    };

    const filters = useFactsheetsFilters(initialState);
    const {
      state,
      setSortBy,
      setSelectedFiltersList,
      setExpandFilters,
      updateMultipleValues
    } = filters;

    const {
      sortBy,
      erasmusCode,
      autumnTermDeadline,
      springTermDeadline,
      country,
      selectedFiltersList,
      expandFilters,
    } = state;

    const handleExpand = (value) => {
        setExpandFilters(value);
    };

    const { t } = useTranslation('translation');

    const navigate = useNavigate();

    const [page, setPage] = useState(localStorage.getItem('pageFactsheets') !== null ? parseInt(JSON.parse(localStorage.getItem('pageFactsheets'))) : 0);

    const [factsheets, setFactsheets] = useState([]);
    
    const { loading, error, data, refetch: refetchFactsheets } = useQuery(GET_FACTSHEETS, {notifyOnNetworkStatusChange: true, fetchPolicy: 'network-only', 
        variables: { 
            page: page,
            pageSize: 30,
            optionalFilters: {
                eCode: erasmusCode,
                studentNomAutumnTerm: autumnTermDeadline,
                studentNomSpringTerm: springTermDeadline,
                country: country
            },
            sortBy: sortBy
        }, onCompleted: (data) => {
            const filteredData = data?.factsheets?.factsheets.filter((item) =>
                item?.institution !== null &&
                item?.institution?.erasmusCode !== null &&
                item?.institution?.name !== null &&
                item?.institution?.country !== null &&
                item?.calendar !== null &&
                item?.calendar?.studentNomAutumnTerm !== null &&
                item?.calendar?.studentNomSpringTerm !== null
            )
            const deDuplicatedData = filteredData.filter((element, index, array) =>
                index === array.findIndex((item) => item.institution?.erasmusCode == element.institution?.erasmusCode)
            );
            setFactsheets(deDuplicatedData);
        }
    });

    const {
        loading: loadingCountries,
        error: errorCountries,
        data: dataCountries,
    } = useQuery(GET_COUNTRIES);

    useEffect(() => {
        const data = localStorage.getItem('selectedFiltersFactsheets');
        
        if (data) {
            if (!Object.values(JSON.parse(data) || {})?.every(x => x === '')) {
                setSelectedFiltersList(JSON.parse(data));
                refetchFactsheets();
            }
        }
    }, []);

    useEffect(() => {
        page !== null && localStorage.setItem('pageFactsheets', JSON.stringify(page));
    }, [page]);

    useEffect(() => {
        localStorage.setItem('selectedFiltersFactsheets', JSON.stringify(selectedFiltersList));
    }, [selectedFiltersList]);

    useEffect(() => {
        sortBy !== null && localStorage.setItem('sortByFactsheets', JSON.stringify(sortBy));
    }, [sortBy]);

    useEffect(() => {
        updateMultipleValues(selectedFiltersList);
        refetchFactsheets();
    }, [
        selectedFiltersList,
        updateMultipleValues,
        refetchFactsheets,
    ]);

    const handleRemoveFilter = (filter, value) => {
        if (filter === "country") {
            let temp = Object.assign({}, selectedFiltersList);
            temp[filter] = temp[filter].filter((i) => i !== value);
            setSelectedFiltersList(temp);
        } else {
            let temp = Object.assign({}, selectedFiltersList);
            temp[filter] = "";
            setSelectedFiltersList(temp);
        }
    };

    const formatDate = (string) => {
        if (!string) return "";
        const [day, month] = string.replace("--", "").split("-");
        const displayDay = parseInt(day);
        const displayMonth = months[parseInt(month) - 1];
        return displayDay && displayMonth ? `${displayDay} ${displayMonth}` : ""
    }
    
    if (loading || loadingCountries) {
        return (
        <TableSkeleton
            columnsPerFieldMap={{
                erasmusCode: "col-2",
                institutionName: "col-2",
                country: "col-1",
                autumnSemesterNominationDeadline: "col-2",
                springSemesterNominationDeadline: "col-2",
                lastChanged: "col-1"
            }}
        />
        );
    }

    if (error || errorCountries)
        return (
        <div className="text-center text-danger mx-auto">{t('errorMessage')}</div>
        );

    return (
        <>
        <main className="h-auto text-center mt-4">
            <CustomTable
                page={page}
                setPage={(p) => setPage(p)}
                pageLimit={30}
                totalCount={data?.factsheets?.totalCount}
                hasPagination={true}
                hasFilters={true}
                expandFilters={expandFilters}
                setExpandFilters={(e) => setExpandFilters(e)}
                filters={selectedFiltersList}
                setFilters={(filter, value) => {handleRemoveFilter(filter, value); setPage(0);}}
                sortBy={sortBy}
                setSortBy={() => setSortBy("")}
                type={"factsheets"}
                customTitle={"no-title"}
                columnsPerFieldMap={{
                    erasmusCode: "col-2",
                    institutionName: "col-2",
                    country: "col-1",
                    autumnSemesterNominationDeadline: "col-1",
                    springSemesterNominationDeadline: "col-1",
                    lastChanged: "col-2"
                }}
                rowClassName="reusable-components-table-row-factsheets"
                headerClassName="reusable-components-table-header-factsheets"
                data={factsheets.map((item) => {
                        return ({
                            erasmusCode: item?.institution?.erasmusCode,
                            institutionName: item?.institution?.name,
                            country: item?.institution?.country,
                            autumnSemesterNominationDeadline: formatDate(item?.calendar?.studentNomAutumnTerm),
                            springSemesterNominationDeadline: formatDate(item?.calendar?.studentNomSpringTerm),
                            lastChanged: new Date(item?.changed).toLocaleDateString("en-GB", {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric'
                            })
                        })
                    })
                }
                onClickHandlers={factsheets.map(({ heiID }) => (e) => {navigate("/factsheets/single-view/" + heiID);})}
            >
                {factsheets?.map((factsheet, index) => (
                  <div
                    key={index}
                    className={`py-3 px-2 justify-content-end justify-content-lg-around align-items-center bg-none bg-md-light button-triplet-requests flex-grow-1 d-flex gap-3 gap-lg-1`}
                  >
                    <ExportFactsheetPdf heiId={factsheet?.heiID} />
                  </div>
                ))}
            </CustomTable>
            <Filters
                expand={expandFilters}
                handleExpand={handleExpand}
                selectedFiltersList={selectedFiltersList}
                setSelectedFiltersList={(e) => {setSelectedFiltersList(e); setPage(0);}}
                sortBy={sortBy}
                setSortBy={(e) => setSortBy(e)}
                countries={dataCountries?.countries}
                type={"factsheets"}
            />
            </main>
        </>
    );
}

export default FactsheetBrowser;